import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Banner, Spinner } from './States';
import type { ColorsResponse, ColorImportPreview } from '../api/types';

/**
 * Cores das skins: importação da planilha e ajuste pontual.
 *
 * O arquivo é lido no navegador e enviado em base64 — sem upload multipart, e
 * sem biblioteca de planilha no bundle do site: quem lê o .xlsx é a API. O
 * cliente final nunca carrega nada disso.
 */
export function ColorsPanel() {
  const [data, setData] = useState<ColorsResponse | null>(null);
  const [previa, setPrevia] = useState<ColorImportPreview | null>(null);
  const [arquivo, setArquivo] = useState<{ nome: string; base64: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Ajuste pontual
  const [skin, setSkin] = useState('');
  const [cor, setCor] = useState('');

  const carregar = useCallback(async () => {
    try {
      setData(await api.adminColors());
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar as cores.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function escolherArquivo(file: File) {
    setErro(null);
    setOk(null);
    setPrevia(null);
    setOcupado(true);
    try {
      const base64 = await lerComoBase64(file);
      setArquivo({ nome: file.name, base64 });
      setPrevia(await api.adminPreviewColors(base64));
    } catch (e) {
      setArquivo(null);
      setErro(e instanceof ApiError ? e.message : 'Não consegui ler o arquivo.');
    } finally {
      setOcupado(false);
    }
  }

  async function importar() {
    if (!arquivo) return;
    setOcupado(true);
    setErro(null);
    try {
      const res = await api.adminImportColors(arquivo.base64);
      setOk(`${res.salvas} cores aplicadas. O filtro do site já está usando a planilha nova.`);
      setArquivo(null);
      setPrevia(null);
      if (inputRef.current) inputRef.current.value = '';
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui importar.');
    } finally {
      setOcupado(false);
    }
  }

  async function salvarManual(remover = false) {
    if (!skin.trim()) return;
    setOcupado(true);
    setErro(null);
    try {
      await api.adminSetColor(skin.trim(), remover ? null : cor.trim());
      setOk(remover ? `Cor de "${skin}" removida.` : `"${skin}" agora é ${cor}.`);
      setSkin('');
      setCor('');
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui salvar.');
    } finally {
      setOcupado(false);
    }
  }

  if (erro && !data) return <Banner kind="error">{erro}</Banner>;
  if (!data) return <Spinner label="Carregando as cores…" />;

  return (
    <div className="panel">
      <h2>Cores das skins</h2>
      <p className="muted">
        De-para de cor por skin, usado no filtro do catálogo. A chave é o nome sem StatTrak™, sem Souvenir e sem o
        desgaste — uma linha da planilha vale para todas as variantes da mesma arte.
      </p>

      {erro && <Banner kind="error">{erro}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}

      <div className="color-stats">
        {data.stats.length === 0 ? (
          <span className="muted">Nenhuma cor cadastrada ainda. Importe a planilha abaixo.</span>
        ) : (
          data.stats.map((c) => (
            <span className="color-stat" key={c.key}>
              <i style={{ background: c.hex }} aria-hidden />
              {c.pt}
              <b>{c.count}</b>
            </span>
          ))
        )}
      </div>

      <h3>Importar planilha</h3>
      <p className="muted">
        Arquivo <b>.xlsx</b> com duas colunas: <b>Skin</b> e <b>Cor</b>. A primeira linha pode ser cabeçalho. A
        planilha substitui o de-para inteiro; ajustes feitos à mão aqui embaixo são preservados.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void escolherArquivo(f);
        }}
      />

      {ocupado && !previa && <Spinner label="Lendo a planilha…" />}

      {previa && (
        <div className="import-preview">
          <div className="stat-row">
            <span>
              <b>{previa.total}</b> linhas lidas
            </span>
            <span className="pos">
              <b>{previa.casaram}</b> casaram com o catálogo
            </span>
            {previa.semCorrespondencia > 0 && (
              <span className="warn">
                <b>{previa.semCorrespondencia}</b> sem correspondência
              </span>
            )}
            {previa.totalIgnoradas > 0 && (
              <span className="warn">
                <b>{previa.totalIgnoradas}</b> ignoradas
              </span>
            )}
          </div>

          <div className="color-stats">
            {previa.cores.map((c) => (
              <span className={`color-stat ${c.conhecida ? '' : 'desconhecida'}`} key={c.color}>
                {c.color}
                <b>{c.count}</b>
                {!c.conhecida && <i title="Cor fora da paleta: vai aparecer em cinza">?</i>}
              </span>
            ))}
          </div>

          {previa.ignoradas.length > 0 && (
            <details className="ignoradas">
              <summary>Ver linhas ignoradas ({previa.totalIgnoradas})</summary>
              <ul>
                {previa.ignoradas.map((l) => (
                  <li key={l.linha}>
                    linha {l.linha}: {l.motivo}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="actions-row">
            <button className="btn primary" onClick={() => void importar()} disabled={ocupado}>
              {ocupado ? 'Importando…' : `Aplicar ${previa.total} cores`}
            </button>
            <button
              className="btn ghost"
              onClick={() => {
                setPrevia(null);
                setArquivo(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <h3>Ajuste pontual</h3>
      <p className="muted">
        Para corrigir uma skin específica sem mexer na planilha. O ajuste fica marcado como manual e sobrevive à
        próxima importação.
      </p>
      <div className="actions-row">
        <input placeholder="AK-47 | Redline" value={skin} onChange={(e) => setSkin(e.target.value)} />
        <select className="select" value={cor} onChange={(e) => setCor(e.target.value)}>
          <option value="">escolha a cor…</option>
          {data.palette.map((c) => (
            <option key={c.key} value={c.key}>
              {c.pt}
            </option>
          ))}
        </select>
        <button className="btn primary" onClick={() => void salvarManual()} disabled={ocupado || !skin.trim() || !cor}>
          Salvar
        </button>
        <button className="btn ghost" onClick={() => void salvarManual(true)} disabled={ocupado || !skin.trim()}>
          Remover cor
        </button>
      </div>
    </div>
  );
}

/** FileReader devolve "data:...;base64,XXX"; a API quer só o XXX. */
function lerComoBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const texto = String(reader.result ?? '');
      const virgula = texto.indexOf(',');
      resolve(virgula >= 0 ? texto.slice(virgula + 1) : texto);
    };
    reader.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
    reader.readAsDataURL(file);
  });
}
